<template>
	<MapLibre ref="mapRef" :locations="mapLocations" location-id-key="id" location-title-key="label" :tool-box="toolBox" :detail-marker-renderer="renderMarkerDetail" @toolbox-action="onToolboxAction" @edit-delivery-location="handleEditDeliveryLocation" @add-new-visit="handleAddNewVisit" @phone-number-copied="handlePhoneNumberCopied" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import MapLibre from './MapLibre.vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getDeliveryLocationTypeIcon, getDeliveryLocationTypeColor, handleEditDeliveryLocation, handleAddNewVisit, handlePhoneNumberCopied, resetDetailMarkerData } from '@/utils/appointmentUtils';
import { useTranslator } from '@/utils/useTranslator';

const t = useTranslator();
const mapRef = ref(null);
const mapLocations = ref([]);
const baseMapLocations = ref([]);
const toolBox = ref(['allDeliveryLocations']);

const iframeWin = iframeDataBridge.getIframeWindow();
const amplify = iframeWin?.amplify;

function buildLocationObject(dl, geoInfo) {
	return {
		id: dl.id,
		label: dl.label || dl.companyName || '',
		deliveryLocationTypeId: dl.deliveryLocationTypeId,
		accountId: dl.accountId,
		phoneNumber: dl.phoneNumber || null,
		addressLine: dl.addressLine,
		addressLine2: dl.addressLine2,
		city: dl.city,
		postcode: dl.postcode,
		country: dl.country,
		icon: getDeliveryLocationTypeIcon(dl),
		color: getDeliveryLocationTypeColor(dl),
		_geo: { latitude: geoInfo.latitude, longitude: geoInfo.longitude },
		lat: geoInfo.latitude,
		lng: geoInfo.longitude,
	};
}

async function onPOSMapUpdate({ deliveryLocationId }) {
	if (!deliveryLocationId) {
		baseMapLocations.value = [];
		mapLocations.value = [];
		return;
	}

	const ecosVM = iframeDataBridge.getEcosVM();
	const dl = (ecosVM?.deliveryLocations || []).find((d) => d.id === deliveryLocationId);
	if (!dl) return;

	const geoInfos = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONGEO');
	const geoInfo = geoInfos?.find((g) => g.deliveryLocationId === deliveryLocationId);
	if (!geoInfo?.latitude || !geoInfo?.longitude) return;

	const location = buildLocationObject(dl, geoInfo);

	baseMapLocations.value = [location];

	// If allDeliveryLocations tool is active, keep the extra locations visible
	const allDLsActive = mapRef.value?.getToolState('allDeliveryLocations')?.active;
	if (allDLsActive) {
		const extras = mapLocations.value.filter((l) => l.id !== deliveryLocationId);
		mapLocations.value = [location, ...extras];
	} else {
		mapLocations.value = [location];
	}

	nextTick(() => {
		mapRef.value?.setCenterZoom?.([geoInfo.longitude, geoInfo.latitude], 14);
	});
}

async function onToolboxAction({ toolId, active }) {
	if (toolId !== 'allDeliveryLocations') return;

	if (!active) {
		mapLocations.value = baseMapLocations.value.slice();
		return;
	}

	const ecosVM = iframeDataBridge.getEcosVM();
	const allDLs = ecosVM?.deliveryLocations || [];
	const geoInfos = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONGEO');

	const extra = allDLs
		.filter((dl) => !baseMapLocations.value.find((b) => b.id === dl.id))
		.map((dl) => {
			const geoInfo = geoInfos?.find((g) => g.deliveryLocationId === dl.id);
			if (!geoInfo?.latitude || !geoInfo?.longitude) return null;
			return buildLocationObject(dl, geoInfo);
		})
		.filter(Boolean);

	mapLocations.value = [...baseMapLocations.value, ...extra];
}

async function renderMarkerDetail(markerData, _iframeDocument, detailMarkerData) {
	resetDetailMarkerData(detailMarkerData, markerData);

	const ecosVM = iframeDataBridge.getEcosVM();
	detailMarkerData.value.title = markerData.label || '';
	detailMarkerData.value.id = markerData.id || null;
	detailMarkerData.value.phoneNumber = markerData.phoneNumber || null;
	detailMarkerData.value.lat = markerData.lat || null;
	detailMarkerData.value.lng = markerData.lng || null;

	const addressParts = [];
	if (markerData.addressLine) addressParts.push(markerData.addressLine);
	if (markerData.addressLine2) addressParts.push(markerData.addressLine2);
	if (markerData.city) addressParts.push(markerData.city);
	if (markerData.postcode) addressParts.push(markerData.postcode);
	if (markerData.country) addressParts.push(markerData.country);
	detailMarkerData.value.address = addressParts.join(', ');

	const fields = {};
	const deliveryLocationTypes = ecosVM?.deliveryLocationTypes || [];
	const deliveryLocationType = deliveryLocationTypes.find((type) => type.id === markerData.deliveryLocationTypeId);
	if (deliveryLocationType) {
		fields[t('Type')] = deliveryLocationType.name || deliveryLocationType.reference || '';
	}
	if (detailMarkerData.value.address) {
		fields[t('Address')] = detailMarkerData.value.address;
	}
	detailMarkerData.value.fields = fields;
	detailMarkerData.value.kpis = [];
}

onMounted(() => {
	if (amplify) {
		amplify.subscribe('evtPOSMapUpdate', onPOSMapUpdate);

		nextTick(() => {
			const posVM = iframeWin?.externis?.sales?.vmSFAPOS;
			if (posVM?.selectedId) {
				onPOSMapUpdate({ deliveryLocationId: posVM.selectedId });
			}
		});
	}
});

onBeforeUnmount(() => {
	if (amplify) {
		amplify.unsubscribe('evtPOSMapUpdate', onPOSMapUpdate);
	}
});
</script>
